import {
  createContext,
  createRef,
  RefObject,
  FC,
  useState,
  useMemo,
  useContext,
  useEffect,
} from 'react'
import { Box, BoxProps, Portal as ChPortal } from '@chakra-ui/react'

type AllowedNames = 'navbar' | 'app' | 'inner nav bar'
type Portals = Record<AllowedNames | string, RefObject<HTMLDivElement> | null>

const PortalContext = createContext<{
  register: (name: AllowedNames) => void
  unregister: (name: AllowedNames) => void
  portals: Portals
}>({
  register: () => null,
  unregister: () => null,
  portals: {},
})

export const PortalProvider: FC<{ children: any }> = ({ children }) => {
  const [portals, setPortals] = useState<Portals>({})
  const register = (id: AllowedNames) => {
    // TODO: Add protections against registering an exit that aleady exists
    const ref = createRef<HTMLDivElement>()
    setPortals((prevPortals) => ({
      ...prevPortals,
      [id]: ref,
    }))
  }
  const unregister = (id: AllowedNames) => {
    setPortals((prevPortals) => ({
      ...prevPortals,
      [id]: null,
    }))
  }

  const values = useMemo(
    () => ({
      register,
      unregister,
      portals,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [portals]
  )

  return (
    <PortalContext.Provider value={values}>{children}</PortalContext.Provider>
  )
}

export const usePortal = (name: AllowedNames) => {
  // TODO: THe magic string approach isn't ideal but there must be a way
  const { register, unregister, portals } = useContext(PortalContext)
  const ref = portals[name]

  const PortalExit = useMemo(
    () => (props: BoxProps) => ref ? <Box ref={ref} {...props} /> : null,
    [ref]
  )

  const Portal: FC<{ children: any }> = useMemo(
    () =>
      ({ children }) =>
        ref ? <ChPortal containerRef={ref}>{children}</ChPortal> : null,
    [ref]
  )

  useEffect(() => {
    register(name)
    return () => {
      unregister(name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { PortalExit, Portal }
}
